import { plainToClass } from "class-transformer";
import { toJS } from "mobx";
import IsdsResponse from "models/isdsResponse";
import EshopRequestBuilder from "../data/eshopRequestBuilder";
import EshopError from "../models/eshopError";
import AuthorizationService from "../services/authorizationService";
import RepositoryBase from "./repositoryBase";
import { parseFileNameFromHeaderResponse, persistedBlobToISDSFormat } from "../utils/fileUtils";
import { PersistedBase64 } from "../models/notice";
import EventBus from "../services/eventBus";
import CustomerRefundApplicationRequest from "../entities/customerRefundApplicationRequest";
import RefundRequestNotice from "../models/refundRequestNotice";
import CustomerRefundApplicationProductionResponse from "entities/customerRefundApplicationProductionResponse";
import BlobWithFileName from "models/blobWithFileName";

const POST_REFUND_REQUEST_REQUEST_FAILURE = "POST_REFUND_REQUEST_REQUEST_FAILURE";
const POST_REFUND_REQUEST_PREVIEW_REQUEST_FAILURE = "POST_REFUND_REQUEST_PREVIEW_REQUEST_FAILURE";
const POST_REFUND_REQUEST_APPLICATIONS_DOCUMENTS_ISDS_FAILURE = "POST_REFUND_REQUEST_APPLICATIONS_DOCUMENTS_ISDS_FAILURE";

export default class RefundRequestRepository extends RepositoryBase<EshopRequestBuilder> {
  constructor(
    protected apiFactory: ReturnType<typeof EshopRequestBuilder.Factory>,
    protected authorizationService: AuthorizationService,
    protected eventBus: EventBus
  ) {
    super(() => apiFactory(), authorizationService, eventBus);
  }

  createRefundRequestObj(refundRequest: RefundRequestNotice, countryId: string, licensePlate: string) {
    const notice = toJS(refundRequest);
    // Convert Notice class to API Request
    const request: CustomerRefundApplicationRequest = {
      chargeOrderSourceType: notice.chargeOrderSourceType,
      reason: notice.reason,
      otherReasonText: notice.otherReasonText,
      bankAccount: notice.bankAccount,
      applicant: {
        ...notice.applicant,
        fullName: notice.applicant.fullName!,
        birthDate: notice.applicant.birthDate!,
        address: notice.applicant.mailingAddress!,
      },
      vehicle: {
        countryId: countryId!,
        licensePlate: licensePlate!,
      },
    };

    return request;
  }

  createRefundRequest(
    refundRequest: RefundRequestNotice,
    countryId: string,
    licensePlate: string
  ): Promise<EshopError | BlobWithFileName> {
    const request = this.createRefundRequestObj(refundRequest, countryId, licensePlate);
    // Convert Notice class to API Request

    return this.callApi(api =>
      api
        .apiVersion(3)
        .one(`charges/customer_refund_applications`)
        .clearPost(request, undefined, undefined, undefined, "application/pdf")
        .then(async (response: any) => {
          if (!response.ok) return plainToClass(EshopError, (await response.json()) as EshopError);

          const fileName = parseFileNameFromHeaderResponse(response.headers.get("Content-Disposition"));
          const blob = await response.blob();

          return { blob: blob, fileName: fileName ?? "download" } as BlobWithFileName;
        })
        .catch(() => {
          throw new EshopError(POST_REFUND_REQUEST_REQUEST_FAILURE);
        })
    );
  }

  getProduction(
    refundRequest: RefundRequestNotice,
    countryId: string,
    licensePlate: string
  ): Promise<EshopError | CustomerRefundApplicationProductionResponse> {
    const request = this.createRefundRequestObj(refundRequest, countryId, licensePlate);

    return this.callApi(() =>
      this.apiFactory()
        .apiVersion(3)
        .one("charges/customer_refund_application_preview")
        .post(request)
        .then(response => {
          return plainToClass(CustomerRefundApplicationProductionResponse, response);
        })
        .catch(() => new EshopError(POST_REFUND_REQUEST_PREVIEW_REQUEST_FAILURE))
    );
  }

  uploadDocuments(ISDSSessionId: string, files: PersistedBase64[]) {
    const data = new FormData();
    files.forEach(persistedBlob => data.append("documentFiles", persistedBlobToISDSFormat(persistedBlob)));

    return this.callApi(api =>
      api
        .apiVersion(3)
        .one(`charges/customer_refund_applications/documents/isds`)
        .postData(data, ISDSSessionId)
        .then(async response => {
          if (!response.ok) return plainToClass(EshopError, (await response.json()) as EshopError);

          return plainToClass(IsdsResponse, (await response.json()) as IsdsResponse);
        })
        .catch(() => {
          throw new EshopError(POST_REFUND_REQUEST_APPLICATIONS_DOCUMENTS_ISDS_FAILURE);
        })
    );
  }
}
